// GAVION commissions data methods (spec §2, §5, §6). Supabase: the public.* RPCs of supabase/schemas/70_gavion_commissions.sql
// (the plan's RPC contract); FakeRest (demo build, tests): the same checks, the same refusal codes and the same
// arithmetic (supabase/functions/_shared/gavion/commissions.ts). The catalog, the plans and the sale steps are plain
// ra-core resources (packages, package_addons, commission_plans, deals): no method here.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { DataProvider, Identifier } from "ra-core";
import { createFakeConfigStore, isObject } from "../settings/fakeConfigStore";
import { torontoDate } from "../domain/time/toronto";
import {
  commissionBaseVisible,
  DEFAULT_ELIGIBLE_SOURCES,
  DEFAULT_VACATION_PAY_PCT,
  dealHasPlan,
  commissionLines,
  employmentEndedOn,
  fromCents,
  toCents,
  vacationPlanPct,
  voidedDebts,
  vacationPay,
  vacationPayText,
  isToSettle,
  maskStatementLine,
  periodAt,
  periodMrr,
  statementFill,
  subscriptionRoot,
  type AddonSnapshot,
  type Billing,
  type CommissionData,
  type EarningKind,
  type EarningLine,
  type ExceptionKind,
  type ExceptionRow,
  type FilledLine,
  type LineKind,
  type PeriodRow,
  type PlanRow,
  type SaleStepsDeal,
  type SettlementKind,
  type StatedLine,
  type SubscriptionRow,
  type VacationPay,
} from "../domain/commissions/commissionModel";
import { proposalAnnual } from "../domain/deals/dealProposal";

/** A refusal: `code` = the RPC's message (draft_exists, …), `detail` = its detail. */
export class CommissionError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail: string = "",
  ) {
    super(code);
  }
}

export type AddonInput = { code: string; qty: number };
export type ServiceInput = { name: string; amount: number };
export type PeriodInput = {
  billing: Billing;
  packageId: number;
  addons: AddonInput[];
  /** Recurring custom services (spec « Corrections »): part of the base. */
  services: ServiceInput[];
  annualAmountPaid: number | null;
};
export type SubscriptionCreateInput = PeriodInput & {
  companyId: number;
  dealId: number | null;
  firstPaidMonth: string;
  note: string | null;
  /** « Reprise » (owner ruling): the company's cancelled subscription this one continues; absent / null = « Nouvelle
   * vente ». */
  continuesSubscriptionId?: number | null;
};
export type SubscriptionChangeInput = PeriodInput & {
  subscriptionId: number;
  fromMonth: string;
};
export type ExceptionInput = {
  subscriptionId: number;
  month: string;
  kind: ExceptionKind;
  amountCollected: number | null;
  note: string | null;
};
export type FeeInput = {
  subscriptionId: number;
  month: string;
  amount: number;
  note: string;
};
export type PreviewLine = EarningLine & {
  company_name: string;
  sales_name: string;
};
export type CommissionChecks = {
  won_without_subscription: {
    deal_id: number;
    deal_name: string;
    company_id: number;
    company_name: string;
    won_at: string | null;
    sales_id: number | null;
  }[];
  without_source: {
    subscription_id: number;
    deal_id: number;
    deal_name: string;
    company_id: number;
    company_name: string;
  }[];
  /** Active subscriptions with no deal that continue nothing: they earn nothing until a deal is linked. */
  without_deal: {
    subscription_id: number;
    company_id: number;
    company_name: string;
  }[];
};
export type StatementRow = {
  id: number;
  sales_id: number;
  cutoff: string;
  status: "draft" | "paid";
  paid_on: string | null;
  note: string | null;
  created_by: number | null;
  created_at: string;
  /** 75_ (review I3): the vacation pay frozen when the statement was marked paid (null: a draft, or paid before 75_). */
  vacation_pay_pct?: number | null;
  vacation_pay_amount?: number | null;
};
export type StatementLineRow = {
  id: number;
  statement_id: number;
  subscription_id: number;
  company_id: number;
  deal_id: number | null;
  month: string;
  payable_from: string;
  kind: LineKind;
  adjusts: EarningKind | null;
  mrr_collected: number | null;
  subscription_month: number | null;
  split_pct: number | null;
  rate_pct: number | null;
  amount: number | null;
  explanation: string;
};
export type StatementFee = {
  subscription_id: number;
  company_id: number;
  company_name: string;
  month: string;
  amount: number;
  note: string;
};
/** A line « Créer un relevé » would take (statementFill), with its company's name. */
export type StatementPreviewLine = FilledLine & {
  sales_id: number;
  company_name: string;
};
export type StatementDetail = {
  statement: StatementRow & { sales_name: string };
  /** base_hidden (owner ruling 2026-10-20): the caller is a Setter / Apporteur only on the line's deal — his amount,
   * no MRR collected, split or rate (private.commission_line_masked). */
  lines: (StatementLineRow & {
    company_name: string;
    base_hidden: boolean;
    /** 75_ (owner ruling 2026-10-21): a negative adjustment, settled with the person, never deducted. */
    to_settle: boolean;
    settled_at: string | null;
    settled_kind: SettlementKind | null;
    settled_note: string | null;
    /** Re-review N2: an open debt a correction now due already cancels (nothing to settle). */
    cancelled: boolean;
  })[];
  fees: StatementFee[];
  /** 75_: the person's « À décider » lines up to the cutoff, on no statement until the split is recorded. */
  pending_decisions: number;
  /** 75_ (owner ruling 2026-10-21): the vacation pay for payroll (information, not in the total); null = no plan. */
  vacation_pay: VacationPay | null;
};

export type CommissionMethods = {
  /** public.commission_plan_new_version: closes the plan the day before `from` and inserts the new row, atomically. */
  commissionPlanNewVersion(
    planId: number,
    from: string,
    plan: Record<string, unknown>,
  ): Promise<number>;
  setAnnualDiscountPct(pct: number): Promise<number>;
  subscriptionCreate(input: SubscriptionCreateInput): Promise<number>;
  subscriptionChange(input: SubscriptionChangeInput): Promise<number>;
  subscriptionCancel(subscriptionId: number, endedMonth: string): Promise<void>;
  subscriptionSetException(input: ExceptionInput): Promise<number>;
  subscriptionDeleteException(id: number): Promise<void>;
  subscriptionAddFee(input: FeeInput): Promise<number>;
  subscriptionDeleteFee(id: number): Promise<void>;
  commissionPreview(
    until: string,
    filter?: { salesId?: number | null; companyId?: number | null },
  ): Promise<PreviewLine[]>;
  commissionChecks(): Promise<CommissionChecks>;
  statementCreate(salesId: number, cutoff: string): Promise<number>;
  /** public.commission_statement_preview: what a statement of the person at `cutoff` would pay now (lines not yet on a
   * paid statement, plus the Corrections and Annulations of paid months), read only, admin only. */
  statementPreview(
    salesId: number,
    cutoff: string,
  ): Promise<StatementPreviewLine[]>;
  statementRefresh(id: number): Promise<number>;
  statementDelete(id: number): Promise<void>;
  statementMarkPaid(
    id: number,
    paidOn: string,
    note: string | null,
  ): Promise<void>;
  statementDetail(id: number): Promise<StatementDetail>;
  /** public.commission_adjustment_settle (75_): an admin records how a negative adjustment of a paid statement was
   * settled with the person (never deducted automatically). */
  adjustmentSettle(
    lineId: number,
    kind: Exclude<SettlementKind, "annule">,
    note: string,
  ): Promise<void>;
  /** export_report('commission_statement'): the CSV rows (admin, audited). `cutoff` = the statement's cutoff, passed as
   * p_from / p_to on Supabase; the FakeRest twin ignores it (it reads the statement by id). */
  statementExport(
    id: number,
    cutoff: string,
  ): Promise<Record<string, unknown>[]>;
};

// ------------------------------------------------------------------------------------------------------------- Supabase
export const createSupabaseCommissionMethods = (
  getClient: () => SupabaseClient,
): CommissionMethods => {
  // The result's shape is the RPC contract (typed by CommissionMethods); `any` lets each method keep its own type.
  const rpc = async (
    fn: string,
    args: Record<string, unknown>,
  ): Promise<any> => {
    const { data, error } = await getClient().rpc(fn, args);
    if (error) throw new CommissionError(error.message, error.details ?? "");
    return data;
  };
  return {
    commissionPlanNewVersion: (planId, from, plan) =>
      rpc("commission_plan_new_version", {
        p_plan_id: planId,
        p_from: from,
        p_plan: plan,
      }),
    setAnnualDiscountPct: (pct) =>
      rpc("set_annual_discount_pct", { p_pct: pct }),
    subscriptionCreate: (i) =>
      rpc("subscription_create", {
        p_company_id: i.companyId,
        p_deal_id: i.dealId,
        p_billing: i.billing,
        p_first_paid_month: i.firstPaidMonth,
        p_package_id: i.packageId,
        p_addons: i.addons,
        p_annual_amount_paid: i.annualAmountPaid,
        p_note: i.note,
        p_services: i.services,
        p_continues: i.continuesSubscriptionId ?? null,
      }),
    subscriptionChange: (i) =>
      rpc("subscription_change", {
        p_subscription_id: i.subscriptionId,
        p_from_month: i.fromMonth,
        p_billing: i.billing,
        p_package_id: i.packageId,
        p_addons: i.addons,
        p_annual_amount_paid: i.annualAmountPaid,
        p_services: i.services,
      }),
    subscriptionCancel: (id, endedMonth) =>
      rpc("subscription_cancel", {
        p_subscription_id: id,
        p_ended_month: endedMonth,
      }),
    subscriptionSetException: (i) =>
      rpc("subscription_set_exception", {
        p_subscription_id: i.subscriptionId,
        p_month: i.month,
        p_kind: i.kind,
        p_amount_collected: i.amountCollected,
        p_note: i.note,
      }),
    subscriptionDeleteException: (id) =>
      rpc("subscription_delete_exception", { p_id: id }),
    subscriptionAddFee: (i) =>
      rpc("subscription_add_fee", {
        p_subscription_id: i.subscriptionId,
        p_month: i.month,
        p_amount: i.amount,
        p_note: i.note,
      }),
    subscriptionDeleteFee: (id) => rpc("subscription_delete_fee", { p_id: id }),
    statementPreview: (salesId, cutoff) =>
      rpc("commission_statement_preview", {
        p_sales_id: salesId,
        p_cutoff: cutoff,
      }),
    commissionPreview: (until, filter = {}) =>
      rpc("commission_preview", {
        p_until: until,
        p_sales_id: filter.salesId ?? null,
        p_company_id: filter.companyId ?? null,
      }),
    commissionChecks: () => rpc("commission_checks", {}),
    statementCreate: (salesId, cutoff) =>
      rpc("commission_statement_create", {
        p_sales_id: salesId,
        p_cutoff: cutoff,
      }),
    statementRefresh: (id) => rpc("commission_statement_refresh", { p_id: id }),
    statementDelete: (id) => rpc("commission_statement_delete", { p_id: id }),
    statementMarkPaid: (id, paidOn, note) =>
      rpc("commission_statement_mark_paid", {
        p_id: id,
        p_paid_on: paidOn,
        p_note: note,
      }),
    statementDetail: (id) => rpc("commission_statement_detail", { p_id: id }),
    adjustmentSettle: (lineId, kind, note) =>
      rpc("commission_adjustment_settle", {
        p_line_id: lineId,
        p_kind: kind,
        p_note: note,
      }),
    statementExport: (id, cutoff) =>
      rpc("export_report", {
        p_table: "commission_statement",
        p_from: cutoff,
        p_to: cutoff,
        p_filters: { statement_id: id },
      }),
  };
};

// ------------------------------------------------------------------------------------------------------------- FakeRest
type Row = Record<string, unknown> & { id: Identifier };
const n = (v: unknown): number => Number(v);
const nn = (v: unknown): number | null =>
  v === null || v === undefined || v === "" ? null : Number(v);
const str = (v: unknown): string | null =>
  v === null || v === undefined ? null : String(v);
/** A real calendar date `YYYY-MM-DD` (the SQL `date` cast refuses 2025-13-01 or 2025-02-31). */
const isDate = (v: unknown): boolean => {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
/** A month = the 1st of a real month. */
const isMonth = (v: unknown): boolean =>
  isDate(v) && (v as string).endsWith("-01");
/** What a statement pays (75_): every amount but the lines to settle. */
const totalPaid = (ls: readonly { kind: LineKind; amount: number | null }[]) =>
  fromCents(
    ls.reduce((t, l) => t + (isToSettle(l) ? 0 : toCents(l.amount ?? 0)), 0),
  );
const fullName = (r?: Row) =>
  r ? `${String(r.first_name ?? "")} ${String(r.last_name ?? "")}`.trim() : "";

export const createFakeCommissionMethods = (
  dp: DataProvider,
  opts: {
    getIdentity: () => Promise<{ id: Identifier } | undefined>;
    /** Defaults to the identity's sales row (administrator !== false); TestAdmin passes its role. */
    isAdmin?: () => Promise<boolean>;
    now?: () => Date;
  },
): CommissionMethods => {
  const { list, readRow, writeKey } = createFakeConfigStore(dp);
  const now = opts.now ?? (() => new Date());
  const today = () => torontoDate(now());
  const stamp = () => now().toISOString();
  const rows = (resource: string) => list<Row>(resource, {}, true);
  const me = async (): Promise<number | null> => {
    const i = await opts.getIdentity();
    return i ? Number(i.id) : null;
  };
  const isAdmin =
    opts.isAdmin ??
    (async () => {
      const id = await me();
      const row = (await rows("sales")).find((r) => n(r.id) === id);
      return row !== undefined && row.administrator !== false;
    });
  const requireAdmin = async () => {
    if (!(await isAdmin())) throw new CommissionError("forbidden");
  };
  const assertMonth = (m: string) => {
    if (!isMonth(m)) throw new CommissionError("invalid_month");
  };

  const subscriptions = async (): Promise<SubscriptionRow[]> =>
    (await rows("subscriptions")).map((r) => ({
      id: n(r.id),
      company_id: n(r.company_id),
      deal_id: nn(r.deal_id),
      billing: r.billing as Billing,
      first_paid_month: String(r.first_paid_month),
      ended_month: str(r.ended_month),
      status: r.status === "cancelled" ? "cancelled" : "active",
      continues_subscription_id: nn(r.continues_subscription_id),
    }));
  const periods = async (): Promise<PeriodRow[]> =>
    (await rows("subscription_periods")).map((r) => ({
      id: n(r.id),
      subscription_id: n(r.subscription_id),
      from_month: String(r.from_month),
      billing: r.billing as Billing,
      package_id: n(r.package_id),
      package_price: n(r.package_price),
      addons: (Array.isArray(r.addons) ? (r.addons as Row[]) : []).map((a) => ({
        code: String(a.code),
        qty: n(a.qty),
        unit_price: n(a.unit_price),
      })),
      services: (Array.isArray(r.services) ? (r.services as Row[]) : []).map(
        (v) => ({
          name: String(v.name),
          amount: n(v.amount),
        }),
      ),
      annual_amount_paid: nn(r.annual_amount_paid),
    }));
  const exceptions = async (): Promise<ExceptionRow[]> =>
    (await rows("subscription_month_exceptions")).map((r) => ({
      id: n(r.id),
      subscription_id: n(r.subscription_id),
      month: String(r.month),
      kind: r.kind as ExceptionKind,
      amount_collected: nn(r.amount_collected),
    }));
  const deals = async (): Promise<SaleStepsDeal[]> =>
    (await rows("deals")).map((r) => ({
      id: n(r.id),
      sales_id: nn(r.sales_id),
      setter_sales_id: nn(r.setter_sales_id),
      demo_by_sales_id: nn(r.demo_by_sales_id),
      sale_source: str(r.sale_source),
      appointment_at: str(r.appointment_at),
      created_at: str(r.created_at),
      demo_held_at: str(r.demo_held_at),
      won_at: str(r.won_at),
      commission_override: isObject(r.commission_override)
        ? r.commission_override
        : null,
    }));
  const plans = async (): Promise<PlanRow[]> =>
    (await rows("commission_plans")).map((r) => ({
      id: n(r.id),
      sales_id: n(r.sales_id),
      effective_from: String(r.effective_from),
      effective_to: str(r.effective_to),
      rate_initial_pct: n(r.rate_initial_pct ?? 10),
      initial_months: n(r.initial_months ?? 12),
      rate_after_pct: n(r.rate_after_pct ?? 5),
      full_share_pct: n(r.full_share_pct ?? 100),
      demo_by_other_split_pct: n(r.demo_by_other_split_pct ?? 50),
      appointment_bonus: n(r.appointment_bonus ?? 100),
      appointment_share_pct: n(r.appointment_share_pct ?? 0),
      appointment_demo_share_pct: nn(r.appointment_demo_share_pct),
      bonus_window_days: n(r.bonus_window_days ?? 180),
      eligible_sources: Array.isArray(r.eligible_sources)
        ? r.eligible_sources.map(String)
        : [...DEFAULT_ELIGIBLE_SOURCES],
      employment_end: str(r.employment_end),
      vacation_pay_pct: n(r.vacation_pay_pct ?? DEFAULT_VACATION_PAY_PCT),
    }));
  const data = async (): Promise<CommissionData> => ({
    subscriptions: await subscriptions(),
    periods: await periods(),
    exceptions: await exceptions(),
    deals: await deals(),
    plans: await plans(),
  });
  const companyName = async () => {
    const cs = await rows("companies");
    return (id: unknown) =>
      String(cs.find((c) => n(c.id) === n(id))?.name ?? "");
  };

  const subscriptionRow = async (id: number) => {
    const row = (await rows("subscriptions")).find((r) => n(r.id) === id);
    if (!row) throw new CommissionError("not_found", "subscription");
    return row;
  };
  const latestFrom = async (sub: SubscriptionRow) =>
    (await periods())
      .filter((p) => p.subscription_id === sub.id)
      .map((p) => p.from_month)
      .sort()
      .at(-1) ?? sub.first_paid_month;
  /** private.put_subscription_period's checks and snapshot. */
  const snapshot = async (input: PeriodInput) => {
    if (input.billing !== "monthly" && input.billing !== "annual")
      throw new CommissionError("invalid_billing");
    const pkg = (await rows("packages")).find(
      (p) => n(p.id) === input.packageId && p.active !== false,
    );
    if (!pkg) throw new CommissionError("invalid_package");
    const annual = input.annualAmountPaid;
    if (
      (input.billing === "annual") !== (annual !== null) ||
      (annual !== null && (annual < 0 || annual > 10_000_000))
    )
      throw new CommissionError("invalid_annual_amount");
    if (input.addons.length > 20) throw new CommissionError("invalid_addons");
    const catalog = await rows("package_addons");
    const addons: AddonSnapshot[] = [];
    for (const a of input.addons) {
      const c = catalog.find(
        (x) =>
          x.code === a.code &&
          x.active !== false &&
          (x.line == null || x.line === pkg.line),
      );
      if (
        !c ||
        !Number.isInteger(a.qty) ||
        a.qty < 1 ||
        a.qty > 9999 ||
        addons.some((x) => x.code === a.code)
      )
        throw new CommissionError("invalid_addons", a.code);
      addons.push({ code: a.code, qty: a.qty, unit_price: n(c.unit_price) });
    }
    // private.subscription_services_check
    const services = input.services ?? [];
    if (
      services.length > 10 ||
      services.some(
        (v) =>
          typeof v.name !== "string" ||
          v.name.trim().length < 1 ||
          v.name.trim().length > 120 ||
          !Number.isFinite(v.amount) ||
          v.amount < 0 ||
          v.amount > 100_000,
      )
    )
      throw new CommissionError("invalid_services");
    return {
      billing: input.billing,
      package_id: n(pkg.id),
      package_price: n(pkg.monthly_price),
      addons,
      services: services.map((v) => ({
        name: v.name.trim(),
        amount: Math.round(v.amount * 100) / 100,
      })),
      annual_amount_paid:
        annual === null ? null : Math.round(annual * 100) / 100,
    };
  };
  const putPeriod = async (
    subscriptionId: number,
    fromMonth: string,
    input: PeriodInput,
  ) => {
    const values = await snapshot(input);
    const existing = (await rows("subscription_periods")).find(
      (p) =>
        n(p.subscription_id) === subscriptionId && p.from_month === fromMonth,
    );
    let id: number;
    if (existing) {
      await dp.update("subscription_periods", {
        id: existing.id,
        data: values,
        previousData: existing,
      });
      id = n(existing.id);
    } else {
      const { data: created } = await dp.create("subscription_periods", {
        data: {
          subscription_id: subscriptionId,
          from_month: fromMonth,
          ...values,
          created_at: stamp(),
        },
      });
      id = n(created.id);
    }
    const sub = await subscriptionRow(subscriptionId);
    await dp.update("subscriptions", {
      id: sub.id,
      data: { billing: input.billing },
      previousData: sub,
    });
    return id;
  };

  const statementRows = async (): Promise<StatementRow[]> =>
    (await rows("commission_statements")).map((r) => ({
      id: n(r.id),
      sales_id: n(r.sales_id),
      cutoff: String(r.cutoff),
      status: r.status === "paid" ? "paid" : "draft",
      paid_on: str(r.paid_on),
      note: str(r.note),
      created_by: nn(r.created_by),
      created_at: String(r.created_at ?? ""),
      vacation_pay_pct: nn(r.vacation_pay_pct),
      vacation_pay_amount: nn(r.vacation_pay_amount),
    }));
  const lineRows = async (): Promise<StatementLineRow[]> =>
    (await rows("commission_statement_lines")).map((r) => ({
      id: n(r.id),
      statement_id: n(r.statement_id),
      subscription_id: n(r.subscription_id),
      company_id: n(r.company_id),
      deal_id: nn(r.deal_id),
      month: String(r.month),
      kind: r.kind as LineKind,
      adjusts: (r.adjusts ?? null) as EarningKind | null,
      payable_from: String(r.payable_from ?? ""),
      mrr_collected: nn(r.mrr_collected),
      subscription_month: nn(r.subscription_month),
      split_pct: nn(r.split_pct),
      rate_pct: nn(r.rate_pct),
      amount: nn(r.amount),
      explanation: String(r.explanation ?? ""),
    }));
  const statementRow = async (id: number) => {
    const row = (await rows("commission_statements")).find(
      (r) => n(r.id) === id,
    );
    if (!row) throw new CommissionError("not_found", "statement");
    return row;
  };
  const draft = async (id: number) => {
    const row = await statementRow(id);
    if (row.status !== "draft") throw new CommissionError("statement_locked");
    return row;
  };
  const deleteLines = async (id: number) => {
    for (const l of (await rows("commission_statement_lines")).filter(
      (x) => n(x.statement_id) === id,
    ))
      await dp.delete("commission_statement_lines", {
        id: l.id,
        previousData: l,
      });
  };
  /** private.commission_statement_rows: stated = the person's other statements (the fill) or his paid ones only (the
   * preview). */
  const fillRows = async (
    salesId: number,
    cutoff: string,
    exclude: number | null,
    paidOnly: boolean,
  ) => {
    const others = new Set(
      (await statementRows())
        .filter(
          (x) =>
            x.sales_id === salesId &&
            x.id !== exclude &&
            (!paidOnly || x.status === "paid"),
        )
        .map((x) => x.id),
    );
    const stated = await withSettlement(
      (await lineRows()).filter((l) => others.has(l.statement_id)),
    );
    const d = await data();
    return statementFill(
      commissionLines(d, cutoff).filter((l) => l.sales_id === salesId),
      stated,
      cutoff,
      d.subscriptions,
    );
  };
  /** The lines with how each was settled (75_, review C1: an « annule » line no longer counts as stated). */
  const withSettlement = async (
    ls: StatementLineRow[],
  ): Promise<(StatementLineRow & StatedLine)[]> => {
    const se = await rows("commission_settlements");
    return ls.map((l) => ({
      ...l,
      settled: (se.find((x) => n(x.line_id) === l.id)?.kind ??
        null) as SettlementKind | null,
    }));
  };
  /** Re-review N2: the person's open debts (on his paid statements) a correction now due already cancels
   * (private.commission_voided_debts at today, Toronto): nothing to settle — closed « annule » at the next payment. */
  const cancelledDebts = async (salesId: number): Promise<Set<number>> => {
    const paidIds = new Set(
      (await statementRows())
        .filter((x) => x.sales_id === salesId && x.status === "paid")
        .map((x) => x.id),
    );
    return new Set(
      voidedDebts(
        commissionLines(await data(), today()).filter(
          (l) => l.sales_id === salesId,
        ),
        await withSettlement(
          (await lineRows()).filter((l) => paidIds.has(l.statement_id)),
        ),
      ),
    );
  };
  /** The comparable form of a statement line (stale_draft, review I1). */
  const lineKey = (l: Omit<FilledLine, never>) =>
    JSON.stringify([
      l.subscription_id,
      l.company_id,
      l.deal_id,
      l.month,
      l.kind,
      l.adjusts,
      l.payable_from,
      l.mrr_collected,
      l.subscription_month,
      l.split_pct,
      l.rate_pct,
      l.amount,
      l.explanation,
    ]);
  /** private.commission_statement_fill. */
  const fill = async (statementId: number, salesId: number, cutoff: string) => {
    const lines = await fillRows(salesId, cutoff, statementId, false);
    for (const l of lines)
      await dp.create("commission_statement_lines", {
        data: { statement_id: statementId, ...l },
      });
    return lines.length;
  };
  const detail = async (id: number): Promise<StatementDetail> => {
    const st = (await statementRows()).find((x) => x.id === id);
    if (!st || !((await isAdmin()) || st.sales_id === (await me())))
      throw new CommissionError("not_found", "statement");
    const name = await companyName();
    const sales = await rows("sales");
    const subs = await subscriptions();
    // private.commission_base_visible: the caller's view of each line's deal (an admin sees every base)
    const viewer = { salesId: await me(), isAdmin: await isAdmin() };
    const deals = await rows("deals");
    const visible = (dealId: number | null) =>
      commissionBaseVisible(
        (dealId === null
          ? null
          : deals.find((d) => n(d.id) === dealId)) as Parameters<
          typeof commissionBaseVisible
        >[0],
        viewer,
      );
    const settlements = await rows("commission_settlements");
    const cancelled = await cancelledDebts(st.sales_id);
    const lines = (await lineRows())
      .filter((l) => l.statement_id === id)
      .map((l) => {
        const se = settlements.find((x) => n(x.line_id) === l.id);
        return {
          ...(visible(l.deal_id)
            ? { ...l, base_hidden: false }
            : maskStatementLine(l)),
          company_name: name(l.company_id),
          // 75_: a negative adjustment is settled with the person, never deducted
          to_settle: isToSettle(l),
          cancelled: cancelled.has(l.id),
          settled_at: se ? String(se.settled_at) : null,
          settled_kind: se ? (se.kind as SettlementKind) : null,
          settled_note: se ? String(se.note) : null,
        };
      })
      .sort(
        (a, b) =>
          a.company_name.localeCompare(b.company_name) ||
          a.month.localeCompare(b.month) ||
          a.id - b.id,
      );
    const fees = (await rows("subscription_one_time_fees"))
      .filter((f) =>
        lines.some(
          (l) =>
            l.subscription_id === n(f.subscription_id) &&
            l.month === f.month &&
            !l.base_hidden,
        ),
      )
      .map((f) => {
        const sub = subs.find((x) => x.id === n(f.subscription_id));
        return {
          subscription_id: n(f.subscription_id),
          company_id: sub?.company_id ?? 0,
          company_name: name(sub?.company_id),
          month: String(f.month),
          amount: n(f.amount),
          note: String(f.note ?? ""),
        };
      });
    return {
      // 75_: the person's « À décider » lines up to the cutoff, left off every statement (a warning)
      pending_decisions: commissionLines(await data(), st.cutoff).filter(
        (l) => l.sales_id === st.sales_id && l.kind === "to_decide",
      ).length,
      // 75_: private.commission_vacation_pay — the plan in force on the cutoff, on the unmasked amounts
      vacation_pay:
        st.status === "paid"
          ? st.vacation_pay_pct == null || st.vacation_pay_pct === 0
            ? null
            : {
                ...(vacationPay(
                  (await lineRows()).filter((l) => l.statement_id === id),
                  st.vacation_pay_pct,
                ) as VacationPay),
                amount: n(st.vacation_pay_amount),
              }
          : vacationPay(
              (await lineRows()).filter((l) => l.statement_id === id),
              vacationPlanPct(await plans(), st.sales_id, st.cutoff),
            ),
      statement: {
        ...st,
        sales_name: fullName(sales.find((x) => n(x.id) === st.sales_id)),
      },
      lines,
      fees,
    };
  };

  return {
    async commissionPlanNewVersion(planId, from, plan) {
      await requireAdmin();
      const old = (await rows("commission_plans")).find(
        (r) => n(r.id) === planId,
      );
      if (!old) throw new CommissionError("not_found", "plan");
      const oldTo = str(old.effective_to);
      if (
        !isDate(from) ||
        from < `${today().slice(0, 7)}-01` ||
        from <= String(old.effective_from) ||
        (oldTo !== null && from > oldTo)
      )
        throw new CommissionError("version_date");
      const newTo =
        typeof plan.effective_to === "string" ? plan.effective_to : null;
      if (
        (await rows("commission_plans")).some(
          (r) =>
            n(r.id) !== planId &&
            n(r.sales_id) === n(old.sales_id) &&
            from <= (str(r.effective_to) ?? "9999-12-31") &&
            String(r.effective_from) <= (newTo ?? "9999-12-31"),
        )
      )
        throw new CommissionError("plan_overlap");
      const prev = new Date(`${from}T00:00:00Z`);
      prev.setUTCDate(prev.getUTCDate() - 1);
      await dp.update("commission_plans", {
        id: old.id,
        data: { effective_to: prev.toISOString().slice(0, 10) },
        previousData: old,
      });
      const created = await dp.create("commission_plans", {
        data: { ...plan, sales_id: old.sales_id, effective_from: from },
      });
      return n(created.data.id);
    },
    async setAnnualDiscountPct(pct) {
      await requireAdmin();
      if (
        !Number.isFinite(pct) ||
        pct < 0 ||
        pct > 100 ||
        Math.round(pct * 100) / 100 !== pct
      )
        throw new CommissionError("invalid_discount");
      const row = await readRow();
      const g = row?.config?.gavion;
      const gavion = isObject(g) ? g : {};
      const b = gavion.billing;
      const billing = isObject(b) ? b : {};
      await writeKey("gavion", {
        ...gavion,
        billing: { ...billing, annual_discount_pct: pct },
      });
      return pct;
    },
    async subscriptionCreate(input) {
      await requireAdmin();
      let i = input;
      if (!(await rows("companies")).some((c) => n(c.id) === i.companyId))
        throw new CommissionError("not_found", "company");
      if (
        i.dealId !== null &&
        !(await rows("deals")).some(
          (d) => n(d.id) === i.dealId && n(d.company_id) === i.companyId,
        )
      )
        throw new CommissionError("invalid_deal");
      // GAVION deal package (76_ twin): no package given → the deal's « Forfait proposé »
      if (!i.packageId && i.dealId !== null) {
        const d = (await rows("deals")).find((x) => n(x.id) === i.dealId);
        if (d?.proposed_package_id != null) {
          const proposed = (d.proposed_addons ?? []) as AddonSnapshot[];
          const billing = (i.billing ?? d.proposed_billing) as Billing;
          i = {
            ...i,
            packageId: n(d.proposed_package_id),
            billing,
            addons:
              i.addons ?? proposed.map((a) => ({ code: a.code, qty: a.qty })),
            annualAmountPaid:
              billing === "annual" &&
              d.proposed_billing === "annual" &&
              i.annualAmountPaid == null
                ? proposalAnnual(
                    n(d.proposed_package_price),
                    proposed,
                    n(d.proposed_discount_pct),
                  )
                : i.annualAmountPaid,
          };
        }
      }
      assertMonth(i.firstPaidMonth);
      if (i.billing !== "monthly" && i.billing !== "annual")
        throw new CommissionError("invalid_billing");
      if (i.note !== null && i.note.length > 2000)
        throw new CommissionError("invalid_note");
      const subs = await subscriptions();
      if (
        subs.some((x) => x.company_id === i.companyId && x.status === "active")
      )
        throw new CommissionError("subscription_exists");
      // never inside a cancelled subscription's billed months (no double base)
      if (
        subs.some(
          (x) =>
            x.company_id === i.companyId &&
            x.status === "cancelled" &&
            x.ended_month !== null &&
            x.ended_month >= i.firstPaidMonth,
        )
      )
        throw new CommissionError("invalid_month", "overlaps_cancelled");
      const continues = i.continuesSubscriptionId ?? null;
      if (
        continues !== null &&
        !subs.some(
          (x) =>
            x.id === continues &&
            x.company_id === i.companyId &&
            x.status === "cancelled" &&
            x.ended_month !== null &&
            x.ended_month < i.firstPaidMonth &&
            !subs.some((y) => y.continues_subscription_id === x.id),
        )
      )
        throw new CommissionError("invalid_continuation");
      // a « Reprise » carries no deal or its chain's root deal; any other subscription needs a deal no subscription
      // uses yet (« Nouvelle vente » = a new deal)
      if (
        continues !== null &&
        i.dealId !== null &&
        i.dealId !==
          subscriptionRoot(subs, subs.find((x) => x.id === continues)!).deal_id
      )
        throw new CommissionError("invalid_continuation", "deal");
      if (
        continues === null &&
        i.dealId !== null &&
        subs.some((x) => x.deal_id === i.dealId)
      )
        throw new CommissionError("deal_already_subscribed");
      await snapshot(i); // the SQL rolls back on a refusal; the twin checks before writing
      const { data: created } = await dp.create("subscriptions", {
        data: {
          company_id: i.companyId,
          deal_id: i.dealId,
          billing: i.billing,
          first_paid_month: i.firstPaidMonth,
          ended_month: null,
          status: "active",
          continues_subscription_id: continues,
          note: i.note?.trim() || null,
          created_at: stamp(),
          created_by: await me(),
        },
      });
      await putPeriod(n(created.id), i.firstPaidMonth, i);
      return n(created.id);
    },
    async subscriptionChange(i) {
      await requireAdmin();
      const sub = (await subscriptions()).find(
        (x) => x.id === i.subscriptionId,
      );
      if (!sub) throw new CommissionError("not_found", "subscription");
      if (sub.status !== "active")
        throw new CommissionError("subscription_cancelled");
      assertMonth(i.fromMonth);
      if (i.fromMonth < (await latestFrom(sub)))
        throw new CommissionError("invalid_month");
      return putPeriod(sub.id, i.fromMonth, i);
    },
    async subscriptionCancel(id, endedMonth) {
      await requireAdmin();
      const sub = (await subscriptions()).find((x) => x.id === id);
      if (!sub) throw new CommissionError("not_found", "subscription");
      if (sub.status !== "active")
        throw new CommissionError("subscription_cancelled");
      assertMonth(endedMonth);
      if (endedMonth < (await latestFrom(sub)))
        throw new CommissionError("invalid_month");
      const row = await subscriptionRow(id);
      await dp.update("subscriptions", {
        id: row.id,
        data: { status: "cancelled", ended_month: endedMonth },
        previousData: row,
      });
    },
    async subscriptionSetException(i) {
      await requireAdmin();
      const sub = (await subscriptions()).find(
        (x) => x.id === i.subscriptionId,
      );
      if (!sub) throw new CommissionError("not_found", "subscription");
      assertMonth(i.month);
      if (
        i.month < sub.first_paid_month ||
        (sub.ended_month !== null && i.month > sub.ended_month)
      )
        throw new CommissionError("invalid_month");
      const amount = i.amountCollected;
      if (
        !["unpaid", "refunded", "partial"].includes(i.kind) ||
        (i.kind === "partial") !== (amount !== null) ||
        (amount !== null && (amount < 0 || amount > 10_000_000)) ||
        (i.note !== null && i.note.length > 500)
      )
        throw new CommissionError("invalid_exception");
      // a partial month never collects more than the month bills (private.subscription_billed_mrr)
      if (amount !== null) {
        const p = periodAt(await periods(), sub.id, i.month);
        if (amount > (p ? periodMrr(p) : 0))
          throw new CommissionError("invalid_exception", "above_mrr");
      }
      const values = {
        kind: i.kind,
        amount_collected: amount,
        source: "admin",
        note: i.note?.trim() || null,
      };
      const existing = (await rows("subscription_month_exceptions")).find(
        (x) => n(x.subscription_id) === sub.id && x.month === i.month,
      );
      if (existing) {
        await dp.update("subscription_month_exceptions", {
          id: existing.id,
          data: values,
          previousData: existing,
        });
        return n(existing.id);
      }
      const { data: created } = await dp.create(
        "subscription_month_exceptions",
        {
          data: {
            subscription_id: sub.id,
            month: i.month,
            ...values,
            created_at: stamp(),
          },
        },
      );
      return n(created.id);
    },
    async subscriptionDeleteException(id) {
      await requireAdmin();
      const row = (await rows("subscription_month_exceptions")).find(
        (x) => n(x.id) === id,
      );
      if (!row) throw new CommissionError("not_found", "exception");
      await dp.delete("subscription_month_exceptions", {
        id: row.id,
        previousData: row,
      });
    },
    async subscriptionAddFee(i) {
      await requireAdmin();
      await subscriptionRow(i.subscriptionId);
      assertMonth(i.month);
      if (
        !Number.isFinite(i.amount) ||
        i.amount < 0 ||
        i.amount > 10_000_000 ||
        i.note.trim() === "" ||
        i.note.trim().length > 500
      )
        throw new CommissionError("invalid_fee");
      const { data: created } = await dp.create("subscription_one_time_fees", {
        data: {
          subscription_id: i.subscriptionId,
          month: i.month,
          amount: i.amount,
          note: i.note.trim(),
          created_at: stamp(),
        },
      });
      return n(created.id);
    },
    async subscriptionDeleteFee(id) {
      await requireAdmin();
      const row = (await rows("subscription_one_time_fees")).find(
        (x) => n(x.id) === id,
      );
      if (!row) throw new CommissionError("not_found", "fee");
      await dp.delete("subscription_one_time_fees", {
        id: row.id,
        previousData: row,
      });
    },
    async commissionPreview(until, filter = {}) {
      await requireAdmin();
      if (!isDate(until)) throw new CommissionError("invalid_cutoff");
      const name = await companyName();
      const sales = await rows("sales");
      return commissionLines(await data(), until)
        .filter(
          (l) =>
            (filter.salesId == null || l.sales_id === filter.salesId) &&
            (filter.companyId == null || l.company_id === filter.companyId),
        )
        .map((l) => ({
          ...l,
          company_name: name(l.company_id),
          sales_name: fullName(sales.find((x) => n(x.id) === l.sales_id)),
        }))
        .sort(
          (a, b) =>
            a.month.localeCompare(b.month) ||
            a.company_name.localeCompare(b.company_name) ||
            a.kind.localeCompare(b.kind) ||
            a.sales_id - b.sales_id,
        );
    },
    async commissionChecks() {
      await requireAdmin();
      const ds = await rows("deals");
      const subs = await subscriptions();
      const name = await companyName();
      // owner ruling 2026-10-21: the owners have no plan — flag only a deal whose people have one for the period
      const ps = await plans();
      const thisMonth = `${today().slice(0, 7)}-01`;
      const hasPlan = (d: Row, x: SubscriptionRow) =>
        dealHasPlan(
          {
            sales_id: nn(d.sales_id),
            setter_sales_id: nn(d.setter_sales_id),
            account_manager_sales_id: nn(d.account_manager_sales_id),
            referrer_sales_id: nn(d.referrer_sales_id),
            demo_by_sales_id: nn(d.demo_by_sales_id),
          },
          ps,
          x.first_paid_month,
          x.ended_month ?? thisMonth,
        );
      return {
        won_without_subscription: ds
          .filter(
            (d) =>
              d.stage === "gagne" &&
              !d.archived_at &&
              !subs.some((x) => x.deal_id === n(d.id)),
          )
          .map((d) => ({
            deal_id: n(d.id),
            deal_name: String(d.name ?? ""),
            company_id: n(d.company_id),
            company_name: name(d.company_id),
            won_at: str(d.won_at),
            sales_id: nn(d.sales_id),
          })),
        // every active subscription is judged by its chain root's deal (private.subscription_root)
        without_source: subs
          .filter((x) => x.status === "active")
          .flatMap((x) => {
            const rootDeal = subscriptionRoot(subs, x).deal_id;
            const d =
              rootDeal === null
                ? undefined
                : ds.find((y) => n(y.id) === rootDeal);
            return d && d.sale_source == null && hasPlan(d, x)
              ? [
                  {
                    subscription_id: x.id,
                    deal_id: n(d.id),
                    deal_name: String(d.name ?? ""),
                    company_id: x.company_id,
                    company_name: name(x.company_id),
                  },
                ]
              : [];
          }),
        without_deal: subs
          .filter(
            (x) =>
              x.status === "active" &&
              subscriptionRoot(subs, x).deal_id === null &&
              // a won deal of the client (the one to link) whose people have a plan
              ds.some(
                (d) =>
                  n(d.company_id) === x.company_id &&
                  d.stage === "gagne" &&
                  !d.archived_at &&
                  hasPlan(d, x),
              ),
          )
          .map((x) => ({
            subscription_id: x.id,
            company_id: x.company_id,
            company_name: name(x.company_id),
          }))
          .sort(
            (a, b) =>
              a.company_name.localeCompare(b.company_name) ||
              a.subscription_id - b.subscription_id,
          ),
      };
    },
    async statementCreate(salesId, cutoff) {
      await requireAdmin();
      if (!isDate(cutoff) || cutoff > today())
        throw new CommissionError("invalid_cutoff");
      if (!(await rows("sales")).some((x) => n(x.id) === salesId))
        throw new CommissionError("not_found", "sales");
      if (
        (await statementRows()).some(
          (x) => x.sales_id === salesId && x.status === "draft",
        )
      )
        throw new CommissionError("draft_exists");
      const { data: created } = await dp.create("commission_statements", {
        data: {
          sales_id: salesId,
          cutoff,
          status: "draft",
          paid_on: null,
          note: null,
          created_by: await me(),
          created_at: stamp(),
        },
      });
      await fill(n(created.id), salesId, cutoff);
      return n(created.id);
    },
    async statementPreview(salesId, cutoff) {
      await requireAdmin();
      if (!isDate(cutoff) || cutoff > today())
        throw new CommissionError("invalid_cutoff");
      if (!(await rows("sales")).some((x) => n(x.id) === salesId))
        throw new CommissionError("not_found", "sales");
      const name = await companyName();
      return (await fillRows(salesId, cutoff, null, true))
        .map((l) => ({
          ...l,
          sales_id: salesId,
          company_name: name(l.company_id),
        }))
        .sort(
          (a, b) =>
            a.company_name.localeCompare(b.company_name) ||
            a.month.localeCompare(b.month) ||
            a.kind.localeCompare(b.kind) ||
            (a.adjusts ?? "").localeCompare(b.adjusts ?? "") ||
            a.subscription_id - b.subscription_id,
        );
    },
    async statementRefresh(id) {
      await requireAdmin();
      const row = await draft(id);
      await deleteLines(id);
      return fill(id, n(row.sales_id), String(row.cutoff));
    },
    async statementDelete(id) {
      await requireAdmin();
      const row = await draft(id);
      await deleteLines(id);
      await dp.delete("commission_statements", {
        id: row.id,
        previousData: row,
      });
    },
    async statementMarkPaid(id, paidOn, note) {
      await requireAdmin();
      const row = await draft(id);
      if (!isDate(paidOn)) throw new CommissionError("invalid_paid_on");
      // a locked record takes no typo: paid on or after the cutoff, never in the future (Toronto)
      if (paidOn > today() || paidOn < String(row.cutoff))
        throw new CommissionError("invalid_date");
      // 75_ (art. 4.3.2): a draft made before the employment end was recorded may pay a month after it: refresh first
      const mine = (await lineRows()).filter((l) => l.statement_id === id);
      const ended = employmentEndedOn(await plans(), n(row.sales_id), mine);
      if (ended !== null) throw new CommissionError("employment_ended", ended);
      // 75_ review I1: only what a refresh would hold now is paid (a draft filled before a change is stale)
      const salesId = n(row.sales_id);
      const cutoff = String(row.cutoff);
      const fresh = (await fillRows(salesId, cutoff, id, false))
        .map(lineKey)
        .sort();
      if (JSON.stringify(mine.map(lineKey).sort()) !== JSON.stringify(fresh))
        throw new CommissionError("stale_draft");
      if (note !== null && note.length > 1000)
        throw new CommissionError("invalid_note");
      // 75_ review C1: the open debts this payment's corrections cancel are closed « annule » (never paid twice)
      const paidIds = new Set(
        (await statementRows())
          .filter(
            (x) => x.sales_id === salesId && x.id !== id && x.status === "paid",
          )
          .map((x) => x.id),
      );
      const paidLines = await withSettlement(
        (await lineRows()).filter((l) => paidIds.has(l.statement_id)),
      );
      for (const lineId of voidedDebts(
        commissionLines(await data(), cutoff).filter(
          (l) => l.sales_id === salesId,
        ),
        paidLines,
      ))
        await dp.create("commission_settlements", {
          data: {
            id: lineId,
            line_id: lineId,
            kind: "annule",
            note: `Annulé par une correction (relevé ${id})`,
            settled_by: await me(),
            settled_at: stamp(),
          },
        });
      // 75_ review I3: the vacation pay is frozen with the statement
      const vacation = vacationPay(
        mine,
        vacationPlanPct(await plans(), salesId, cutoff),
      );
      await dp.update("commission_statements", {
        id: row.id,
        data: {
          status: "paid",
          paid_on: paidOn,
          note: note?.trim() || null,
          vacation_pay_pct: vacation?.pct ?? null,
          vacation_pay_amount: vacation?.amount ?? null,
        },
        previousData: row,
      });
    },
    statementDetail: detail,
    async adjustmentSettle(lineId, kind, note) {
      await requireAdmin();
      const l = (await lineRows()).find((x) => x.id === lineId);
      if (!l) throw new CommissionError("not_found", "line");
      const st = (await statementRows()).find((x) => x.id === l.statement_id);
      if (!isToSettle(l) || st?.status !== "paid")
        throw new CommissionError("not_to_settle");
      // re-review 2 NEW-1: money actually recovered (retenue / rembourse) is still recorded — the next fill pays it
      // back; only a waiver of what a correction already cancels is refused
      if (
        kind === "abandonne" &&
        (await cancelledDebts(st.sales_id)).has(lineId)
      )
        throw new CommissionError("not_open");
      if (!["retenue", "rembourse", "abandonne"].includes(kind))
        throw new CommissionError("invalid_settlement");
      if (note === null || note.trim() === "" || note.length > 1000)
        throw new CommissionError("invalid_note");
      if (
        (await rows("commission_settlements")).some(
          (x) => n(x.line_id) === lineId,
        )
      )
        throw new CommissionError("already_settled");
      await dp.create("commission_settlements", {
        data: {
          id: lineId,
          line_id: lineId,
          kind,
          note: note.trim(),
          settled_by: await me(),
          settled_at: stamp(),
        },
      });
    },
    // The cutoff argument only feeds the Supabase side's export_report window (p_from = p_to = the statement's
    // cutoff); the twin reads the statement by id and ignores it.
    async statementExport(id) {
      await requireAdmin();
      const d = await detail(id);
      const st = d.statement;
      const common = {
        statement_id: st.id,
        person: st.sales_name,
        cutoff: st.cutoff,
        status: st.status,
        paid_on: st.paid_on,
      };
      return [
        ...d.lines.map((l) => ({
          ...common,
          company: l.company_name,
          month: l.month.slice(0, 7),
          kind: l.kind,
          payable_from: l.payable_from,
          subscription_month: l.subscription_month,
          mrr_collected: l.mrr_collected,
          split_pct: l.split_pct,
          rate_pct: l.rate_pct,
          amount: l.amount,
          explanation: l.explanation,
          // 75_ review I2: a negative adjustment is to settle, never deducted (outside « Total versé »)
          to_settle: l.to_settle,
        })),
        ...d.fees.map((f) => ({
          ...common,
          company: f.company_name,
          month: f.month.slice(0, 7),
          kind: "one_time_fee",
          payable_from: null,
          subscription_month: null,
          mrr_collected: null,
          split_pct: null,
          rate_pct: null,
          amount: null,
          explanation: `Frais unique (information, hors base) : ${f.amount.toFixed(2)} $ · ${f.note}`,
          to_settle: false,
        })),
      ]
        .sort(
          (a, b) =>
            a.company.localeCompare(b.company) ||
            a.month.localeCompare(b.month),
        )
        .map((r): Record<string, unknown> => r)
        .concat([
          // 75_ review I2: « Total versé » (the lines to settle are not in it)
          {
            ...common,
            company: null as unknown as string,
            month: null as unknown as string,
            kind: "total_paid",
            payable_from: null,
            subscription_month: null,
            mrr_collected: null,
            split_pct: null,
            rate_pct: null,
            amount: totalPaid(d.lines),
            explanation: "Total versé (hors « À régler »)",
            to_settle: false,
          },
        ])
        .concat(
          // 75_: the vacation pay, information for payroll (no amount), last
          d.vacation_pay === null
            ? []
            : [
                {
                  ...common,
                  company: null as unknown as string,
                  month: null as unknown as string,
                  kind: "vacation_pay",
                  payable_from: null,
                  subscription_month: null,
                  mrr_collected: null,
                  split_pct: null,
                  rate_pct: null,
                  amount: null,
                  explanation: vacationPayText(d.vacation_pay),
                  to_settle: false,
                },
              ],
        );
    },
  };
};
